// utils/units/format.js
//
// The one formatter both repos print measurements with: "150 lb",
// "8 lb 7 oz", "37.2 degrees C", "1,234.5 kg". en-US digit grouping, an ASCII space
// between number and symbol (none before a `joined` one: "45.0" and a degree
// sign), half-away-from-zero rounding, and never a
// floating-point artifact. Dependency-free and vendored into keepr-web (see
// registry.js for the rule).
//
// Display resolution order (chooseDisplayUnit):
//   1. prefs.unit / prefs.precision   explicit overrides (display templates)
//   2. prefs.unitSystem               the user preference: the family's
//                                     system default, rules skipped -- and
//                                     the element's own unit where that
//                                     default would round the value to zero
//   3. options.displayRules           ordered, bounds in defaultUnit, first
//                                     match wins
//   4. options.display                { unit, precision }
//   5. options.defaultUnit
// A unit of 'entered' is the stored unit; 'auto' is the largest non-composite
// unit whose magnitude is at least 1, chosen from options.units when set and
// otherwise from the default unit's system AND FAMILY -- see autoUnit.
//
// `options.fractions` (KPR-1025) spells the number as a half, third, quarter
// or eighth when it is near one ("1 1/2 cup", "3/4 in", the inches of
// "5 ft 3 1/2 in"), in whichever unit was chosen above; a number near none
// keeps its decimal at the chosen precision. fraction.js holds the rule and
// its tolerance. `prefs.fractions === false` turns it off for one rendering.

'use strict';

const { getMeasure, getUnit, unitsOf, isComposite, isUnitOf, isMultiMeasure, measureOfUnit, scopeOptions, isOwnUnitId, unitIn } = require('./registry');
const { toBase, baseOperator, canConvert } = require('./convert');
const { readMeasurement } = require('./normalize');
const Fraction = require('./fraction');

const MAX_PRECISION = 6;

function isPrecision(n) {
	return Number.isInteger(n) && n >= 0 && n <= MAX_PRECISION;
}

/** Round half away from zero at `decimals` places, without binary drift. */
function roundHalfAway(n, decimals) {
	const places = isPrecision(decimals) ? decimals : 0;
	const abs = Math.abs(n);
	const text = String(abs);
	let rounded;
	if (text.indexOf('e') === -1) {
		rounded = Number(Math.round(Number(text + 'e' + places)) + 'e-' + places);
	} else {
		const factor = Math.pow(10, places);
		rounded = Math.round(abs * factor) / factor;
	}
	if (!Number.isFinite(rounded)) return n;
	return n < 0 ? -rounded : rounded;
}

// "1.5e+21" -> "1500000000000000000000" (integers only; the input is >= 1e21).
function expandExponent(text) {
	const m = text.match(/^(\d+)(?:\.(\d+))?e\+(\d+)$/);
	if (!m) return text;
	const digits = m[1] + (m[2] || '');
	const zeros = Number(m[3]) - (m[2] ? m[2].length : 0);
	return zeros >= 0 ? digits + '0'.repeat(zeros) : digits;
}

function groupInteger(digits) {
	return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** An already-rounded number as "1,234.5" (en-US grouping, fixed decimals); '' when not finite. */
function formatNumber(n, decimals) {
	if (typeof n !== 'number' || !Number.isFinite(n)) return '';
	const places = isPrecision(decimals) ? decimals : 0;
	const rounded = roundHalfAway(n, places);
	if (rounded === 0) return places ? '0.' + '0'.repeat(places) : '0';
	const abs = Math.abs(rounded);
	// toFixed switches to exponent notation at 1e21; spell such integers out.
	const fixed = abs >= 1e21
		? expandExponent(String(abs)) + (places ? '.' + '0'.repeat(places) : '')
		: abs.toFixed(places);
	const dot = fixed.indexOf('.');
	const whole = dot === -1 ? fixed : fixed.slice(0, dot);
	const fraction = dot === -1 ? '' : fixed.slice(dot);
	return (rounded < 0 ? '-' : '') + groupInteger(whole) + fraction;
}

/**
 * formatInUnit(base, unitId, precision, fractions, options) -> "8 lb 7 oz" / "68.0 kg" / ''.
 * `precision` is the decimals of the number (of the minor part for a
 * composite). A minor part that rounds up to a whole major carries.
 * `fractions` true spells a number near a half, third, quarter or eighth as
 * that fraction ("1 1/2 cup"; a composite's minor part: "5 ft 3 1/2 in"),
 * and any other number as before. `options`, the element's, name its OWN
 * units (KPR-1109): without them an own unit prints as nothing, as any
 * unknown unit does. A unit with a plural (a pinch, an own unit) prints it
 * unless the number shown is more than 0 and at most 1: "2.0 pinches",
 * "1/2 pinch", "1.0 pinch", "0.0 pinches".
 */
function formatInUnit(base, unitId, precision, fractions, options) {
	const unit = unitIn(unitId, options);
	if (!unit || typeof base !== 'number' || !Number.isFinite(base)) return '';
	const places = isPrecision(precision) ? precision : (unit.parts ? 0 : 1);
	const magnitude = unit.fromBase(base);
	if (!Number.isFinite(magnitude)) return '';

	if (!unit.parts) {
		const fraction = fractions === true ? Fraction.formatFraction(magnitude, { group: true }) : null;
		let word = unit.symbol;
		if (unit.plural) {
			const near = fraction !== null ? Fraction.nearestFraction(magnitude) : null;
			const shown = Math.abs(near ? near.whole + near.numerator / near.denominator : roundHalfAway(magnitude, places));
			word = shown > 0 && shown <= 1 ? unit.symbol : unit.plural;
		}
		// A plane angle's degree sign sits against its number ("45.0°").
		return (fraction !== null ? fraction : formatNumber(magnitude, places)) + (unit.joined ? '' : ' ') + word;
	}

	const majorUnit = getUnit(unit.parts[0]);
	const minorUnit = getUnit(unit.parts[1]);
	const negative = magnitude < 0;
	const abs = Math.abs(magnitude);
	let major = Math.floor(abs);
	const minorExact = (abs - major) * unit.minorPerMajor;
	const nearest = fractions === true ? Fraction.nearestFraction(minorExact) : null;
	let minor;
	let minorText;
	if (nearest) {
		minor = nearest.whole + nearest.numerator / nearest.denominator;
		minorText = Fraction.spellFraction(nearest, { group: true });
	} else {
		minor = roundHalfAway(minorExact, places);
		minorText = formatNumber(minor, places);
	}
	if (minor >= unit.minorPerMajor) {
		major += 1;
		minor = 0;
		minorText = nearest ? '0' : formatNumber(0, places);
	}
	const sign = negative && (major > 0 || minor > 0) ? '-' : '';
	return sign + formatNumber(major, 0) + ' ' + majorUnit.symbol + ' ' + minorText + ' ' + minorUnit.symbol;
}

// Rule bounds and stored bases meet at exact thresholds ("20 lb" stored vs a
// `lt: 20` rule), so compare them snapped to 1e-9 like the KQL compiler does.
const SNAP = 1e9;
function snap(n) {
	return Math.round(n * SNAP) / SNAP;
}

// The bounds are numbers in defaultUnit, so a reciprocal default unit turns
// each one around on base ("lt: 6" L/100km is a base above 16.67 km/L).
function ruleMatches(when, rawBase, defaultUnit) {
	if (!when || typeof when !== 'object') return false;
	const base = snap(rawBase);
	let any = false;
	for (const key of ['lt', 'lte', 'gt', 'gte']) {
		if (typeof when[key] !== 'number' || !Number.isFinite(when[key])) continue;
		any = true;
		const bound = snap(toBase(when[key], defaultUnit));
		const op = baseOperator(key, defaultUnit);
		if (op === 'lt' && !(base < bound)) return false;
		if (op === 'lte' && !(base <= bound)) return false;
		if (op === 'gt' && !(base > bound)) return false;
		if (op === 'gte' && !(base >= bound)) return false;
	}
	return any;
}

// The candidates for 'auto': the element's allowlist when it has one, else
// the units of the default unit's system (850 m becomes 1.2 km, never 0.5 mi)
// and always of the default unit's FAMILY (a 500 gal tank is never re-read as
// 2.5 yd3). Composites are never picked; temperature has no meaningful
// 'auto'; a count has no larger unit to be expressed in at all.
function autoUnit(measure, base, options, defaultUnitId) {
	if (measure.id === 'temperature') return null;
	// Nor has a measure with a reciprocal unit: "the largest unit whose number
	// is at least 1" means nothing when one unit's number runs backwards
	// (fuel economy), so the element reads in its own unit.
	if (measure.units.some(id => getUnit(id).decreasing)) return null;
	const fallback = getUnit(defaultUnitId);
	// "3 tablets" is not 3 of anything else. Leaving it in its own unit is
	// the only honest automatic answer.
	if (fallback && fallback.convertible === false) return null;
	const all = unitsOf(measure.id).filter(u => !u.parts && u.convertible !== false);
	const allowlist = options && Array.isArray(options.units) && options.units.length ? options.units : null;
	const system = (fallback || {}).system;
	const family = (fallback || {}).family;
	let candidates = allowlist ? all.filter(u => allowlist.indexOf(u.id) !== -1) : [];
	// An allowlist of composites only ("lb-oz") still displays in its own system.
	if (!candidates.length) candidates = all.filter(u => u.system === system);
	if (!candidates.length) candidates = all;
	// Within one measure a family is a different KIND of spelling. Skipped
	// when it would empty the set, so a family with a single member still
	// resolves to something.
	const sameFamily = candidates.filter(u => u.family === family);
	if (sameFamily.length) candidates = sameFamily;
	// Some spellings are for entry, not for automatic display (cL, PB, ft/s).
	// The element's own default unit is never filtered out -- but it is only
	// KEPT, not preferred, so a family whose members all read absurdly as one
	// another is a family, not a row of flags (registry.js).
	const displayable = candidates.filter(u => u.autoDisplay !== false || u.id === defaultUnitId);
	if (displayable.length) candidates = displayable;
	if (!candidates.length) return null;
	// Largest first: the scale of a unit is what one of it is in base terms.
	candidates = candidates.slice().sort((a, b) => b.toBase(1) - a.toBase(1));
	for (const unit of candidates) {
		if (Math.abs(unit.fromBase(base)) >= 1) return unit.id;
	}
	return candidates[candidates.length - 1].id;
}

/**
 * The unit a viewer's `unitSystem` preference resolves to, taken from the
 * FAMILY of the element's own default unit: a cubic-yard element under
 * 'metric' reads in m3, not in the 605 gallons `systemDefaults` alone would
 * give. `systemDefaults` stays the answer for a family with no entry, and
 * null means "the preference has nothing to say here" -- the caller keeps
 * the element's own unit.
 */
function systemDefaultUnit(measure, defaultUnitId, system) {
	// A measure whose two system defaults name the same unit has no metric /
	// imperial split at all: seconds are seconds, bytes are bytes, a calorie
	// is a calorie and a tablet is a tablet. Answering "B" for a 4 GB element
	// because someone prefers metric is not a preference being honoured.
	if (measure.systemDefaults.metric === measure.systemDefaults.imperial) return null;
	const family = (getUnit(defaultUnitId) || {}).family;
	const byFamily = measure.familyDefaults && family && measure.familyDefaults[family];
	const id = (byFamily && byFamily[system]) || measure.systemDefaults[system];
	return isUnitOf(id, measure.id) ? id : null;
}

/**
 * True when `base` is a real quantity that would print as nothing but zeros
 * in `unitId` at `places` decimals -- "0.0 m3" for a 350 cm3 engine, "0.0 kg"
 * for a 500 mcg dose, "0 ft 0 in" for a 50 micron coating. A genuine zero is
 * not a loss, and an OFFSET scale has a meaningful zero of its own
 * (-17.8 degrees C really is 0 F), so neither counts.
 */
function roundsToZero(base, unitId, places) {
	const unit = getUnit(unitId);
	if (!unit || typeof base !== 'number' || !Number.isFinite(base) || base === 0) return false;
	if (unit.fromBase(0) !== 0) return false;
	const magnitude = Math.abs(unit.fromBase(base));
	if (!Number.isFinite(magnitude)) return false;
	if (!unit.parts) return roundHalfAway(magnitude, places) === 0;
	// A composite prints two numbers and loses the value only when both go.
	const major = Math.floor(magnitude);
	return major === 0 && roundHalfAway((magnitude - major) * unit.minorPerMajor, places) === 0;
}

// 'auto' within the units `unitId` converts to: the largest whose number is at
// least 1, else the smallest. Null when it converts to none but itself.
function autoAmong(unitId, base, measure) {
	if (typeof base !== 'number' || !Number.isFinite(base)) return null;
	const candidates = unitsOf(measure.id)
		.filter(u => !u.parts && u.autoDisplay !== false && (u.id === unitId || canConvert(unitId, u.id)))
		.sort((a, b) => b.toBase(1) - a.toBase(1));
	if (candidates.length < 2) return null;
	for (const unit of candidates) {
		if (Math.abs(unit.fromBase(base)) >= 1) return unit.id;
	}
	return candidates[candidates.length - 1].id;
}

function resolveDisplayUnit(unitId, stored, measure, options, fallbackId) {
	const entered = getUnit(stored.unit);
	const own = entered && entered.measure === measure.id ? entered.id : null;
	// A count of one thing is never shown as a count of another (KPR-1109):
	// "2 pinches" under an element whose default unit is each stays 2 pinches,
	// and 2 dozen is never "2.0 each". A unit the stored one cannot convert to
	// gives way to the stored unit, whatever chose it (a display unit, a rule,
	// a preference, the default unit).
	const shown = (id) => {
		if (!own || !id || id === own) return id;
		return canConvert(own, id) ? id : own;
	};
	if (unitId === 'entered') return own || fallbackId;
	if (unitId === 'auto') {
		const picked = autoUnit(measure, stored.base, options, fallbackId) || fallbackId;
		// Automatic among the units the stored one converts to (a count
		// rate's other times: 0.01 tablet/s reads 36.0 tablet/h, never "0.0
		// tablet/s" nor "36.0 each/h").
		if (own && picked && picked !== own && !canConvert(own, picked)) return autoAmong(own, stored.base, measure) || own;
		return picked;
	}
	const unit = getUnit(unitId);
	return shown(unit && unit.measure === measure.id ? unit.id : fallbackId);
}

/**
 * chooseDisplayUnit(stored, options, prefs) -> { unit, precision } | null
 * The resolved unit id (never 'auto'/'entered') and decimals to print with.
 */
function chooseDisplayUnit(stored, options, prefs) {
	const opts = options || {};
	// An element that allows several measures (KPR-1085): each value chooses
	// as a single-measure element of ITS unit's measure would (registry.js
	// scopeOptions) -- the primary by every rule above, another measure as
	// entered, or 'auto' and the viewer's unit system within its own family.
	if (isMultiMeasure(opts)) {
		const read = readMeasurement(stored, opts);
		if (!read || typeof read.base !== 'number') return null;
		const scoped = scopeOptions(opts, measureOfUnit(read.unit), read.unit);
		return scoped ? chooseDisplayUnit(read, scoped, prefs) : null;
	}
	const p = prefs || {};
	// One OWN unit (KPR-1109, registry.js scopeOptions): it converts to
	// nothing, so it shows as entered whatever a display unit, `auto` or a
	// viewer's unit system says, at the display's (or the viewer's) precision.
	if (isOwnUnitId(opts.measure)) {
		const own = readMeasurement(stored, opts);
		if (!own || typeof own.base !== 'number' || own.unit !== opts.measure) return null;
		const shown = opts.display && typeof opts.display === 'object' ? opts.display : {};
		return { unit: opts.measure, precision: isPrecision(p.precision) ? p.precision : (isPrecision(shown.precision) ? shown.precision : 1) };
	}
	const measure = getMeasure(opts.measure);
	const m = readMeasurement(stored, opts);
	if (!measure || !m || typeof m.base !== 'number') return null;

	const defaultUnit = getUnit(opts.defaultUnit) && getUnit(opts.defaultUnit).measure === measure.id
		? opts.defaultUnit
		: measure.canonical;
	const display = opts.display && typeof opts.display === 'object' ? opts.display : {};

	let unitId = typeof display.unit === 'string' && display.unit ? display.unit : defaultUnit;
	let precision = isPrecision(display.precision) ? display.precision : null;

	if (typeof p.unit === 'string' && p.unit) {
		unitId = p.unit;
	} else if (p.unitSystem === 'metric' || p.unitSystem === 'imperial') {
		// A family/system default is ONE fixed unit, chosen with no regard to
		// magnitude, so it can round the value clean away: a 350 cm3 engine
		// reading "0.0 m3", a 500 mcg dose "0.0 kg", a 12 mm2 wound "0.0 m2".
		// A preference is a preference about SPELLING; it may never cost the
		// reader the number. When it would, 'auto' inside the element's own
		// system and family is the honest answer -- and the conversions modal
		// still lists every other unit either way.
		unitId = systemDefaultUnit(measure, defaultUnit, p.unitSystem) || defaultUnit;
		const places = isPrecision(p.precision) ? p.precision
			: isPrecision(precision) ? precision
				: (isComposite(unitId) ? 0 : 1);
		if (roundsToZero(m.base, unitId, places)) unitId = autoUnit(measure, m.base, opts, defaultUnit) || defaultUnit;
	} else if (Array.isArray(opts.displayRules)) {
		for (const rule of opts.displayRules) {
			if (!rule || typeof rule !== 'object') continue;
			if (!ruleMatches(rule.when, m.base, defaultUnit)) continue;
			if (typeof rule.unit === 'string' && rule.unit) unitId = rule.unit;
			if (isPrecision(rule.precision)) precision = rule.precision;
			break;
		}
	}
	if (isPrecision(p.precision)) precision = p.precision;

	const unit = resolveDisplayUnit(unitId, m, measure, opts, defaultUnit);
	if (precision === null) precision = isComposite(unit) ? 0 : 1;
	return { unit, precision };
}

/**
 * formatMeasurement(stored, options, prefs) -> string ('' when empty).
 * `stored` is the item value ({ value, unit, base }, a bare number, or a
 * parseable string); `options` the element options (its own units named in
 * `ownUnits`, KPR-1109: "2.0 smidgens"); `prefs` an optional
 * { unitSystem, unit, precision, fractions }.
 */
function formatMeasurement(stored, options, prefs) {
	const opts = options || {};
	const m = readMeasurement(stored, opts);
	if (!m || typeof m.base !== 'number') return '';
	const choice = chooseDisplayUnit(m, opts, prefs);
	if (!choice) return '';
	const fractions = opts.fractions === true && !(prefs && prefs.fractions === false);
	return formatInUnit(m.base, choice.unit, choice.precision, fractions, isOwnUnitId(choice.unit) ? opts : null);
}

module.exports = {
	MAX_PRECISION,
	roundHalfAway,
	formatNumber,
	formatInUnit,
	chooseDisplayUnit,
	formatMeasurement,
};
